import {
  isLatinAmericanSpanish,
  LATIN_AMERICAN_SPANISH_LOCALE,
} from '@packages/domain/utils/locale/latinAmericanSpanish';
import some from 'lodash/some';
import type React from 'react';
import type { JSX } from 'react';
import { Children, isValidElement } from 'react';

import { errorNotifier } from 'utils/errorNotifier/errorNotifier';

export const rtlLocales = ['ar'];
export const TURKISH_LOCALE = 'tr-TR';
export const THAI_LOCALE = 'th-TH';
export const GATED_LOCALES: string[] = [
  THAI_LOCALE,
  LATIN_AMERICAN_SPANISH_LOCALE,
];

// FYI this type name is used in a Support RegEx to extract the locales
export type SupportedLocalesKeys =
  | 'en'
  | 'zh-CN'
  | 'es-ES'
  | 'es-419'
  | 'ko-KR'
  | 'ja-JP'
  | 'fr-FR'
  | 'it-IT'
  | 'pt-PT'
  | 'de-DE'
  | 'id-ID'
  | 'vi-VN'
  | 'ar-SA'
  | 'tr-TR'
  | 'th-TH';

export type Translations = Record<string, string | Record<string, string>>;
type Locales = { [key in SupportedLocalesKeys]: string };

export const locales: Locales = {
  en: 'EN',
  'zh-CN': 'zh-CN',
  'es-ES': 'es-ES',
  [LATIN_AMERICAN_SPANISH_LOCALE]: 'es-419',
  'ko-KR': 'ko-KR',
  'ja-JP': 'ja-JP',
  'fr-FR': 'fr-FR',
  'it-IT': 'it-IT',
  'pt-PT': 'pt-PT',
  'de-DE': 'de-DE',
  'id-ID': 'id-ID',
  'vi-VN': 'vi-VN',
  'ar-SA': 'ar-SA',
  [TURKISH_LOCALE]: 'tr-TR',
  [THAI_LOCALE]: 'th-TH',
};

export const cmsLocales = [
  'en',
  'zh-CN',
  'es-ES',
  'ko-KR',
  'ja-JP',
  'fr-FR',
  'pt-PT',
];

export const localeCodes = Object.keys(locales);
export const extractLocaleCode = (locale: string) => locale.split('-')[0];
export const getKeyFromLocaleCode = (code: string) =>
  isLatinAmericanSpanish(code)
    ? LATIN_AMERICAN_SPANISH_LOCALE
    : localeCodes.find(c => c.startsWith(code));
export const isRtlLocale = (locale: string) =>
  rtlLocales.includes(extractLocaleCode(locale));

export type I18n = {
  t: (
    key: string,
    opts?: {
      [key: string]: string | React.JSX.Element;
    },
  ) => string;
  currentLocale: SupportedLocalesKeys;
  localeCode: string;
  isRtl: boolean;
  setLocale: (locale: SupportedLocalesKeys) => void;
};

export const isValidLocale = (locale: string) => {
  return Object.keys(locales).includes(locale);
};

function isNotAKey(key: unknown) {
  if (!key) {
    return true;
  }
  if (typeof key !== 'string') {
    return true;
  }
  if (key.includes(' ')) {
    return true;
  }

  return false;
}

export function translate(
  key: unknown,
  attrs: Record<string, string | JSX.Element> = {},
  keys: Translations,
  fallbackKeys: Translations,
) {
  try {
    if (isNotAKey(key)) {
      return key as string;
    }
    return translateStringKey(key as string, attrs, keys, fallbackKeys);
  } catch (error) {
    errorNotifier.notify(error, { key });
    return key as string;
  }
}

function translateStringKey(
  key: string,
  attrs: Record<string, string | JSX.Element> = {},
  keys: Translations,
  fallbackKeys: Translations,
) {
  if (
    /* biome-ignore lint/suspicious/noPrototypeBuiltins: false positive */
    (!Object.prototype.hasOwnProperty.call(keys, key) ||
      /* biome-ignore lint/suspicious/noPrototypeBuiltins: false positive */
      !Object.prototype.hasOwnProperty.call(fallbackKeys, key)) &&
    process.env.NODE_ENV === 'development'
  ) {
    console.info(`[translation] '${key}' key not found!`);
  }

  const translation = keys[key] || fallbackKeys[key];

  if (translation === undefined) {
    return key;
  }

  const optionsRegex = /%{([a-zA-Z0-9]+)}/g;
  const hasReactValues = some(Object.values(attrs), o => isValidElement(o));

  if (hasReactValues) {
    const chunks = (translation as string)
      .split(optionsRegex)
      .map((chunk: string) => {
        return attrs[chunk] || chunk;
      });

    return Children.toArray(chunks).filter(el => !!el) as unknown as string;
  }

  return (translation as string).replace(
    optionsRegex,
    (_str, match) => (attrs[match] as string) || '',
  );
}
